import React, { useState, useCallback } from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { Lock, Settings } from 'lucide-react';
import { ProcessorNodeData } from '../../types/pipeline';
import { usePipelineBuilderStore } from '../../store/pipelineBuilderStore';
import { useUIStore } from '../../store';
import { QuickEditDialog } from './QuickEditDialog';

export const SystemInputNode: React.FC<NodeProps<ProcessorNodeData>> = ({ id, data, selected }) => {
  const selectNode = usePipelineBuilderStore((state) => state.selectNode);
  const theme = useUIStore((state) => state.theme);
  const [showQuickEdit, setShowQuickEdit] = useState(false);

  const handleSettingsClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    selectNode(id);
  }, [id, selectNode]);

  const handleNodeClick = useCallback((e: React.MouseEvent) => {
    // Don't open quick edit if clicking on buttons or handles
    const target = e.target as HTMLElement;
    if (target.closest('button') || target.closest('.react-flow__handle')) {
      return;
    }
    e.stopPropagation(); // Prevent node selection
    setShowQuickEdit(true);
  }, []);

  const handleCloseQuickEdit = useCallback(async () => {
    setShowQuickEdit(false);
    
    // Trigger validation if pipeline was previously validated
    const state = usePipelineBuilderStore.getState();
    if (state.validatedAt) {
      try {
        const { buildPipelineSteps } = require('../../utils/executionFlow');
        const { apiService } = require('../../services/api');
        const steps = buildPipelineSteps(state.nodes, state.edges);
        const results = await apiService.validatePipeline({ steps });
        usePipelineBuilderStore.getState().setValidationResults(results);
      } catch (error) {
        console.error('Auto-revalidation after closing quick edit failed:', error);
      }
    }
  }, []);

  return (
    <>
      <div
        className={`
          bg-green-50 dark:bg-green-900/20
          border-2 
          ${selected ? 'border-blue-500 shadow-lg' : 'border-green-500'}
          rounded-lg w-[280px] shadow-md
          transition-all duration-200
          cursor-pointer
        `}
        onClick={handleNodeClick}
        data-testid="system-input-node"
      >
      {/* Header with execution flow handle */}
      <div className="bg-green-500 dark:bg-green-600 text-white px-3 py-2 rounded-t-md flex items-center justify-between relative">
        <button
          onClick={handleSettingsClick}
          className="p-1 hover:bg-green-600 dark:hover:bg-green-700 rounded transition-colors"
          title="Open detailed settings"
        >
          <Settings className="w-4 h-4" />
        </button>
        
        <div className="flex items-center gap-2 flex-1 justify-center">
          <span className="text-lg">⚙️</span>
          <span className="font-semibold text-sm">
            {data.name || 'Workflow Input (System)'}
          </span>
          <Lock className="w-3 h-3" />
        </div>
        
        {/* Execution flow handle (next_node) - right side, square shape */}
        <Handle
          type="source"
          position={Position.Right}
          id="next_node"
          className="execution-handle !w-3 !h-3 !border-2 !border-slate-500 dark:!border-gray-300"
            style={{
              background: theme === 'dark' ? '#e5e5e5' : '#64748b',
              borderRadius: '2px',
              right: '-6px',
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 10,
            }}
          data-testid="next-node-handle"
        />
      </div>

      {/* Body */}
      <div className="bg-white dark:bg-gray-800 p-3 rounded-b-md">
        {/* Description */}
        {data.description && (
          <div className="text-xs text-gray-600 dark:text-gray-400 mb-3">
            {data.description}
          </div>
        )}

        {/* Outputs Section */}
        <div className="space-y-1">
          <div className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">
            OUTPUTS:
          </div>
          {data.output_keys.map((key, index) => (
            <div
              key={`output-${key}`}
              className="flex items-center justify-between text-xs"
            >
              <span className="text-gray-700 dark:text-gray-300 font-medium">
                {key}
              </span>
              <Handle
                type="source"
                position={Position.Right}
                id={`output-${key}`}
                className="!bg-green-500 !w-3 !h-3 !border-2 !border-green-600 relative"
                style={{ 
                  position: 'relative',
                  top: 'auto',
                  right: '-12px',
                  transform: 'none'
                }}
              />
            </div>
          ))}
        </div>
      </div>
    </div>

    {/* Quick Edit Dialog */}
    {showQuickEdit && (
      <QuickEditDialog nodeId={id} onClose={handleCloseQuickEdit} />
    )}
  </>
  );
};
